import "react-datepicker/dist/react-datepicker.css";

import React, { useContext, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { Calendar } from "lucide-react";
import { fr } from "date-fns/locale";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "../utils/toast";

import { api } from "../api/api";
import AuthContext from "../contexts/AuthCtx";
import DashboardLayout from "../components/Dashboard/DashboardLayout";
import {
  FieldLabel,
  OnboardingInput,
  OnboardingSelect,
  PrimaryButton,
} from "../components/Onboarding/OnboardingFields";
import { ClipLoader } from "react-spinners";

function Settings() {
  const AuthCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [form, setForm] = useState({
    birthName: "",
    firstName: "",
    nationality: "",
    title: "",
    birthNumber: "",
    dateOfBirth: null,
    birthPlace: "",
  });

  useEffect(() => {
    if (!AuthCtx.userToken) return;

    api
      .get("/auth/getUser", {
        headers: { Authorization: `Bearer ${AuthCtx.userToken}` },
      })
      .then(({ data }) => {
        const user = data?.user;
        const info = user?.user_info || {};
        setEmail(user?.email || "");
        setForm({
          birthName: info.birthName || "",
          firstName: info.firstName || "",
          nationality: info.nationality || "",
          title: info.title || "",
          birthNumber: info.birth_department_Number || "",
          dateOfBirth: info.date_of_birth
            ? new Date(info.date_of_birth.split("T")[0])
            : null,
          birthPlace: info.place_of_birth || "",
        });
        setIsLoading(false);
      })
      .catch(() => {
        toast.error("Impossible de charger tes informations.");
        setIsLoading(false);
      });
  }, [AuthCtx.userToken]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.dateOfBirth) {
      toast.error("La date de naissance est requise.");
      return;
    }

    setIsSaving(true);
    const user_info = {
      firstName: form.firstName.trim(),
      birthName: form.birthName.trim(),
      nationality: form.nationality,
      title: form.title,
      date_of_birth: moment(form.dateOfBirth).format("YYYY-MM-DD"),
      place_of_birth: form.birthPlace.trim(),
      birth_department_Number: form.birthNumber.trim(),
    };

    try {
      await api.post(
        "auth/updateUser",
        { user_info: JSON.stringify(user_info) },
        {
          headers: {
            Authorization: `Bearer ${AuthCtx.userToken}`,
          },
        }
      );
      AuthCtx.updateHandler();
      toast.success("Informations mises à jour.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde.");
    } finally {
      setIsSaving(false);
    }
  };

  const addLetterHandler = () => {
    api
      .post("letter/create", { title: "Sans titre" })
      .then((res) => navigate(`/letter/${res.data.id}`))
      .catch((err) => toast.error(err.response?.data?.message || "Erreur"));
  };

  return (
    <DashboardLayout onNewLetter={addLetterHandler}>
      {isLoading ? (
        <div className="flex h-[60vh] items-center justify-center">
          <ClipLoader color="#96B2A3" size={40} />
        </div>
      ) : (
        <div className="mx-auto max-w-3xl">
          <h1 className="mb-2 font-logo text-3xl text-black sm:text-4xl">
            Réglages
          </h1>
          <p className="mb-8 font-josefin text-sm text-secondaryText/70">
            Ces informations sont utilisées pour détecter ton décès. Assure-toi
            qu&apos;elles correspondent à celles de tes documents d&apos;identité.
          </p>

          <form
            onSubmit={handleSave}
            className="rounded-xl border border-greyLight bg-white p-6 sm:p-8"
          >
            <h2 className="mb-6 font-amaranth text-lg text-secondaryText">
              Informations personnelles
            </h2>

            <div className="space-y-5">
              <div>
                <FieldLabel htmlFor="email">Adresse e-mail</FieldLabel>
                <input
                  id="email"
                  type="email"
                  value={email}
                  readOnly
                  className="w-full rounded-lg border border-greyLight bg-backLight/50 py-3 px-4 font-josefin text-secondaryText/70 outline-none cursor-not-allowed"
                />
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <FieldLabel htmlFor="birthName" required>
                    Nom de naissance
                  </FieldLabel>
                  <OnboardingInput
                    id="birthName"
                    value={form.birthName}
                    onChange={(e) => updateField("birthName", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="firstName" required>
                    Prénom(s)
                  </FieldLabel>
                  <OnboardingInput
                    id="firstName"
                    value={form.firstName}
                    onChange={(e) => updateField("firstName", e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <FieldLabel htmlFor="nationality" required>
                  Nationalité
                </FieldLabel>
                <OnboardingSelect
                  id="nationality"
                  value={form.nationality}
                  onChange={(e) => updateField("nationality", e.target.value)}
                  placeholder="Nationalité"
                >
                  <option value="Fr">Française</option>
                </OnboardingSelect>
              </div>

              <div>
                <FieldLabel htmlFor="title" required>
                  Civilité
                </FieldLabel>
                <OnboardingSelect
                  id="title"
                  value={form.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  placeholder="Civilité"
                >
                  <option value="M">Homme</option>
                  <option value="F">Femme</option>
                </OnboardingSelect>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <div>
                  <FieldLabel htmlFor="birthNumber" required>
                    N° département
                  </FieldLabel>
                  <OnboardingInput
                    id="birthNumber"
                    value={form.birthNumber}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) {
                        updateField("birthNumber", e.target.value);
                      }
                    }}
                    inputMode="numeric"
                    maxLength={3}
                    required
                  />
                  <p className="mt-1.5 font-josefin text-xs text-textMuted">
                    &quot;99&quot; si tu es né(e) à l&apos;étranger
                  </p>
                </div>
                <div>
                  <FieldLabel htmlFor="dateOfBirth" required>
                    Date de naissance
                  </FieldLabel>
                  <div className="relative">
                    <DatePicker
                      id="dateOfBirth"
                      selected={form.dateOfBirth}
                      onChange={(date) => updateField("dateOfBirth", date)}
                      locale={fr}
                      dateFormat="dd/MM/yyyy"
                      maxDate={new Date()}
                      placeholderText="jj/mm/aaaa"
                      showYearDropdown
                      scrollableMonthYearDropdown
                      yearDropdownItemNumber={100}
                      showMonthDropdown
                      dropdownMode="select"
                      className="w-full rounded-lg border border-greyLight bg-white py-3 pl-4 pr-12 font-josefin text-secondaryText outline-none focus:border-primaryText"
                      wrapperClassName="w-full"
                    />
                    <Calendar
                      size={18}
                      className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-greyLight"
                    />
                  </div>
                </div>
                <div>
                  <FieldLabel htmlFor="birthPlace" required>
                    Lieu de naissance
                  </FieldLabel>
                  <OnboardingInput
                    id="birthPlace"
                    value={form.birthPlace}
                    onChange={(e) => updateField("birthPlace", e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <PrimaryButton
                type="submit"
                variant="purple"
                disabled={isSaving}
              >
                {isSaving ? "Enregistrement…" : "Enregistrer"}
              </PrimaryButton>
            </div>
          </form>
        </div>
      )}
    </DashboardLayout>
  );
}

export default Settings;
